import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Calendar, Key, Lock, Plus, Search, Server, Trash2, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { useToast } from "../../../hooks/use-toast";
import { Device } from "../interfaces/EndpointInterfaces";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import {
    deleteCredential,
    getGatewayCredentialsList,
    GatewayCredentialAuthType,
    GatewayCredentialProtocol,
    storeGatewayCredential,
} from "./Api/credentialapi";
import { CredentialInterface } from "./interfaces/credentialinterface";

interface GatewayCredentialDetailsProps {
    device: Device;
}

type GatewayCredentialAuth = GatewayCredentialAuthType;
type GatewayProtocol = GatewayCredentialProtocol;

interface GatewayCredentialRecord extends CredentialInterface {
    protocol?: string;
    protocolAuth?: string;
}

interface GatewayCredentialFormState {
    label: string;
    username: string;
    protocol: GatewayProtocol;
    authType: GatewayCredentialAuth;
    privateKey: string;
    passphrase: string;
    password: string;
    confirmPassword: string;
}

const initialFormState: GatewayCredentialFormState = {
    label: "",
    username: "",
    protocol: "SSH",
    authType: "Key",
    privateKey: "",
    passphrase: "",
    password: "",
    confirmPassword: "",
};

const normalizeGatewayCredentials = (response: unknown): GatewayCredentialRecord[] => {
    if (Array.isArray(response)) {
        return response as GatewayCredentialRecord[];
    }

    if (
        response &&
        typeof response === "object" &&
        Array.isArray((response as { content?: unknown[] }).content)
    ) {
        return (response as { content: GatewayCredentialRecord[] }).content;
    }

    return [];
};

const getDeviceOs = (device: Device) => {
    const normalizedOs = (device.os || device.OperatingSystem || "").toLowerCase();
    return normalizedOs.includes("windows") ? "WINDOWS" : "LINUX";
};

const formatCreatedAt = (value?: string) => {
    if (!value) {
        return "N/A";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

export default function GatewayCredentialDetails({ device }: GatewayCredentialDetailsProps) {
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const { email, userId } = useUserStore((state) => state.user);
    const { toast } = useToast();

    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [credentials, setCredentials] = useState<GatewayCredentialRecord[]>([]);
    const [isLoadingCredentials, setIsLoadingCredentials] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [deletingCredentialLabel, setDeletingCredentialLabel] = useState<string>("");
    const [credentialToDelete, setCredentialToDelete] =
        useState<GatewayCredentialRecord | null>(null);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
    const [formState, setFormState] = useState<GatewayCredentialFormState>(initialFormState);

    const openAddCredentialDialog = () => {
        resetForm();
        setIsAddDialogOpen(true);
    };

    const fetchGatewayCredentials = useCallback(async () => {
        if (!tenantName.trim() || !device.id) {
            setCredentials([]);
            setIsLoadingCredentials(false);
            return;
        }

        setIsLoadingCredentials(true);
        try {
            const response = await getGatewayCredentialsList(tenantName, device.id);
            setCredentials(normalizeGatewayCredentials(response));
        } catch (error: any) {
            const description =
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to fetch gateway credentials.";
            toast({
                title: "Unable to load credentials",
                description,
                variant: "destructive",
            });
            setCredentials([]);
        } finally {
            setIsLoadingCredentials(false);
        }
    }, [tenantName, device.id, toast]);

    useEffect(() => {
        fetchGatewayCredentials();
    }, [fetchGatewayCredentials]);

    const filteredCredentials = useMemo(() => {
        const query = deferredSearchTerm.trim().toLowerCase();
        if (!query) {
            return credentials;
        }

        return credentials.filter((credential) => {
            const searchableValues = [
                credential.credentialLabel,
                credential.credentialUsername,
                credential.protocol,
                credential.protocolType,
                credential.createdBy,
            ]
                .map((value) => (value ?? "").toString().toLowerCase());

            return searchableValues.some((value) => value.includes(query));
        });
    }, [credentials, deferredSearchTerm]);

    const credentialSummary = useMemo(() => {
        let ssh = 0;
        let rdp = 0;

        credentials.forEach((credential) => {
            const protocol = (credential.protocol || "").toLowerCase();
            if (protocol === "ssh") {
                ssh += 1;
            } else if (protocol === "rdp") {
                rdp += 1;
            }
        });

        return {
            total: credentials.length,
            ssh,
            rdp,
        };
    }, [credentials]);

    const isPasswordMode =
        formState.protocol === "RDP" || formState.authType === "Password";

    const isFormValid = useMemo(() => {
        if (!formState.label.trim() || !formState.username.trim()) {
            return false;
        }

        if (isPasswordMode) {
            return (
                formState.password.trim().length > 0 &&
                formState.confirmPassword.trim().length > 0 &&
                formState.password === formState.confirmPassword
            );
        }

        return formState.privateKey.trim().length > 0;
    }, [formState, isPasswordMode]);

    const resetForm = () => {
        setFormState(initialFormState);
    };

    const handleProtocolChange = (value: string) => {
        const nextProtocol: GatewayProtocol = value === "RDP" ? "RDP" : "SSH";

        setFormState((prev) => {
            if (nextProtocol === "RDP") {
                return {
                    ...prev,
                    protocol: "RDP",
                    authType: "Password",
                    privateKey: "",
                    passphrase: "",
                };
            }

            return {
                ...prev,
                protocol: "SSH",
                authType: prev.authType === "Password" ? "Key" : prev.authType,
            };
        });
    };

    const handleAuthTypeChange = (value: string) => {
        const nextAuthType: GatewayCredentialAuth =
            value === "Password" ? "Password" : "Key";

        setFormState((prev) =>
            nextAuthType === "Key"
                ? {
                    ...prev,
                    authType: "Key",
                    password: "",
                    confirmPassword: "",
                }
                : {
                    ...prev,
                    authType: "Password",
                    privateKey: "",
                    passphrase: "",
                }
        );
    };

    const handleDeleteGatewayCredential = async (credential: GatewayCredentialRecord) => {
        const label = (credential.credentialLabel || "").trim();

        if (!label) {
            toast({
                title: "Delete failed",
                description: "Credential label is missing for this row.",
                variant: "destructive",
            });
            return;
        }

        if (!device.id) {
            toast({
                title: "Delete failed",
                description: "Device ID is missing.",
                variant: "destructive",
            });
            return;
        }

        setDeletingCredentialLabel(label);

        try {
            await deleteCredential({
                label,
                deviceId: device.id,
            });

            toast({
                title: "Credential deleted",
                description: `Credential "${label}" has been removed.`,
            });

            await fetchGatewayCredentials();
        } catch (error: any) {
            const description =
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to delete gateway credential.";

            toast({
                title: "Delete failed",
                description,
                variant: "destructive",
            });
        } finally {
            setDeletingCredentialLabel("");
            setCredentialToDelete(null);
        }
    };

    const handleStoreCredential = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!tenantName.trim()) {
            toast({
                title: "Tenant missing",
                description: "Select a tenant before storing credentials.",
                variant: "destructive",
            });
            return;
        }

        if (!email || userId == null) {
            toast({
                title: "User context missing",
                description: "Unable to identify user details to store credentials.",
                variant: "destructive",
            });
            return;
        }

        if (isPasswordMode && formState.password !== formState.confirmPassword) {
            toast({
                title: "Validation error",
                description: "Password and confirm password must match.",
                variant: "destructive",
            });
            return;
        }

        const isSshKeyMode =
            formState.protocol === "SSH" && formState.authType === "Key";
        const privateKey = formState.privateKey;
        const privateString = privateKey.includes("\\n")
            ? privateKey.replace(/\\n/g, "\n")
            : privateKey;

        setIsSubmitting(true);
        try {
            await storeGatewayCredential({
                label: formState.label.trim(),
                username: formState.username.trim(),
                password: isPasswordMode
                    ? formState.password
                    : isSshKeyMode
                        ? privateString
                        : "",
                retypePassword: isPasswordMode ? formState.confirmPassword : "",
                tenantName,
                os: getDeviceOs(device),
                computerId: device.id,
                createdBy: email,
                userId: String(userId),
                fromGateway: true,
                protocol: formState.protocol,
                protocolAuth: formState.protocol === "RDP" ? "Password" : formState.authType,
                privateKey: isSshKeyMode ? privateString : "",
                passPharse:
                    isSshKeyMode ? formState.passphrase : "",
            });

            toast({
                title: "Credential stored",
                description: `Credential "${formState.label}" has been stored for this gateway.`,
            });

            resetForm();
            setSearchTerm("");
            setIsAddDialogOpen(false);
            await fetchGatewayCredentials();
        } catch (error: any) {
            const description =
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to store gateway credential.";

            toast({
                title: "Store failed",
                description,
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 p-6" data-testid="gateway-credentials-tab">


            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="relative w-full md:max-w-md">
                            <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search credentials by label, username, protocol..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                className="pl-10"
                                data-testid="input-search-gateway-credentials"
                            />
                        </div>

                        <Button
                            onClick={openAddCredentialDialog}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="button-add-gateway-credential"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Credential
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Server className="w-5 h-5" />
                        Credentials ({filteredCredentials.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/60 hover:bg-muted/60">
                                <TableHead className="font-semibold text-foreground">Name</TableHead>
                                <TableHead className="font-semibold text-foreground">Protocol</TableHead>
                                <TableHead className="font-semibold text-foreground">Auth Type</TableHead>
                                <TableHead className="font-semibold text-foreground">Created By</TableHead>
                                <TableHead className="font-semibold text-foreground">Created At</TableHead>
                                <TableHead className="font-semibold text-foreground text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoadingCredentials ? (
                                Array.from({ length: 5 }).map((_, index) => (
                                    <TableRow key={`gateway-credential-skeleton-${index}`}>
                                        <TableCell colSpan={6} className="py-4">
                                            <Skeleton className="w-full h-6" />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : filteredCredentials.length > 0 ? (
                                filteredCredentials.map((credential) => (
                                    <TableRow key={credential.id} data-testid={`gateway-credential-${credential.id}`}>
                                        <TableCell>
                                            <div className="font-medium">{credential.credentialUsername} </div>
                                            <div className="text-xs text-muted-foreground">Label: {credential.rawLabel || credential.credentialUsername}</div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {(credential.protocol || "N/A").toUpperCase()}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary">
                                                {(credential.protocolType || "N/A").toUpperCase()}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{credential.createdBy || "N/A"}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2 text-sm">
                                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                                {formatCreatedAt(credential.createdDate)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                                onClick={() => setCredentialToDelete(credential)}
                                                disabled={Boolean(deletingCredentialLabel)}
                                                data-testid={`button-delete-gateway-credential-${credential.id}`}
                                            >
                                                <Trash2 className="w-4 h-4 " />

                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center gap-2">
                                            <Key className="w-5 h-5" />
                                            <p>No gateway credentials found.</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <AlertDialog
                open={Boolean(credentialToDelete)}
                onOpenChange={(open) => {
                    if (!open && !deletingCredentialLabel) {
                        setCredentialToDelete(null);
                    }
                }}
            >
                <AlertDialogContent data-testid="dialog-confirm-delete-gateway-credential">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Gateway Credential</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete credential "
                            {credentialToDelete?.credentialLabel ||
                                credentialToDelete?.credentialUsername ||
                                "this credential"}
                            "? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={Boolean(deletingCredentialLabel)}
                            data-testid="button-cancel-delete-gateway-credential"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (credentialToDelete) {
                                    void handleDeleteGatewayCredential(credentialToDelete);
                                }
                            }}
                            disabled={Boolean(deletingCredentialLabel)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete-gateway-credential"
                        >
                            {deletingCredentialLabel ? "Deleting..." : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog
                open={isAddDialogOpen}
                onOpenChange={(open) => {
                    setIsAddDialogOpen(open);
                    if (!open && !isSubmitting) {
                        resetForm();
                    }
                }}
            >
                <DialogContent
                    className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                    data-testid="dialog-add-gateway-credential"
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="w-5 h-5" />
                            Add Gateway Credential
                        </DialogTitle>
                        <DialogDescription>
                            Configure protocol and authentication details for this gateway credential.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleStoreCredential} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="gateway-credential-label">Credential Label</Label>
                                <Input
                                    id="gateway-credential-label"
                                    value={formState.label}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            label: event.target.value,
                                        }))
                                    }
                                    placeholder="e.g., gateway-admin"
                                    data-testid="input-gateway-credential-label"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="gateway-credential-protocol">Protocol</Label>
                                <Select
                                    value={formState.protocol}
                                    onValueChange={handleProtocolChange}
                                >
                                    <SelectTrigger id="gateway-credential-protocol" data-testid="select-gateway-credential-protocol">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="SSH">SSH</SelectItem>
                                        <SelectItem value="RDP">RDP</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {formState.protocol === "SSH" && (
                            <div className="space-y-2">
                                <Label>Authentication Type</Label>
                                <RadioGroup
                                    value={formState.authType}
                                    onValueChange={handleAuthTypeChange}
                                    className="grid grid-cols-2 gap-4"
                                    data-testid="radio-gateway-auth-type"
                                >
                                    <div className="flex items-center gap-2 p-3 border rounded-md">
                                        <RadioGroupItem value="Key" id="gateway-auth-key" />
                                        <Label htmlFor="gateway-auth-key" className="cursor-pointer">
                                            SSH Key
                                        </Label>
                                    </div>
                                    <div className="flex items-center gap-2 p-3 border rounded-md">
                                        <RadioGroupItem value="Password" id="gateway-auth-password" />
                                        <Label htmlFor="gateway-auth-password" className="cursor-pointer">
                                            Password
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="gateway-credential-username">Username</Label>
                            <Input
                                id="gateway-credential-username"
                                value={formState.username}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        username: event.target.value,
                                    }))
                                }
                                placeholder="Enter username"
                                data-testid="input-gateway-credential-username"
                            />
                        </div>

                        {isPasswordMode ? (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="gateway-credential-password" className="flex items-center gap-2">
                                        <Lock className="w-4 h-4" />
                                        Password
                                    </Label>
                                    <Input
                                        id="gateway-credential-password"
                                        type="password"
                                        value={formState.password}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                password: event.target.value,
                                            }))
                                        }
                                        placeholder="Enter password"
                                        data-testid="input-gateway-credential-password"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="gateway-credential-confirm-password"
                                        className="flex items-center gap-2"
                                    >
                                        <Lock className="w-4 h-4" />
                                        Confirm Password
                                    </Label>
                                    <Input
                                        id="gateway-credential-confirm-password"
                                        type="password"
                                        value={formState.confirmPassword}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                confirmPassword: event.target.value,
                                            }))
                                        }
                                        placeholder="Confirm password"
                                        data-testid="input-gateway-credential-confirm-password"
                                    />
                                    {formState.password &&
                                        formState.confirmPassword &&
                                        formState.password !== formState.confirmPassword && (
                                            <p className="text-sm text-red-600">
                                                Password and confirm password must match.
                                            </p>
                                        )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="gateway-credential-private-key" className="flex items-center gap-2">
                                        <Key className="w-4 h-4" />
                                        Private Key
                                    </Label>
                                    <Textarea
                                        id="gateway-credential-private-key"
                                        value={formState.privateKey}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                privateKey: event.target.value,
                                            }))
                                        }
                                        placeholder="-----BEGIN PRIVATE KEY-----"
                                        className="min-h-[120px] font-mono text-sm"
                                        data-testid="textarea-gateway-credential-private-key"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="gateway-credential-passphrase" className="flex items-center gap-2">
                                        <User className="w-4 h-4" />
                                        Passphrase (Optional)
                                    </Label>
                                    <Input
                                        id="gateway-credential-passphrase"
                                        type="password"
                                        value={formState.passphrase}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                passphrase: event.target.value,
                                            }))
                                        }
                                        placeholder="Enter optional passphrase"
                                        data-testid="input-gateway-credential-passphrase"
                                    />
                                </div>
                            </>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    resetForm();
                                    setIsAddDialogOpen(false);
                                }}
                                disabled={isSubmitting}
                                data-testid="button-cancel-gateway-credential"
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                disabled={!isFormValid || isSubmitting}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="button-store-gateway-credential"
                            >
                                {isSubmitting ? "Storing..." : "Store Credential"}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
